import type { GenerationDetail, GenerationItem, GenerationSummary } from '../model/types';

/**
 * Вид статусов «Недавно добавленных» (ТЗ главной § 22.4, § 22.5, § 23.2) — чистые функции без
 * alias-импортов и без i18n: значок, тон и ключи текстов; подставляет тексты виджет.
 */

/** «Дольше обычного» — после 3 мин от нажатия этого захода или от первого показа «генерируется». */
export const SLOW_AFTER_MS = 3 * 60_000;

export type GlyphKind = 'generating' | 'ready' | 'failed' | 'removed';
/** accent — `accent-hi`, warn — `warn`, muted — приглушённый, plain — цвет строки. */
export type StatusTone = 'accent' | 'warn' | 'muted' | 'plain';

export interface TextRef {
  key: string;
  params?: Record<string, string | number>;
}

export const isGenerationActive = (s: Pick<GenerationSummary, 'status' | 'processing'>) =>
  s.status === 'generating' || s.processing;

export const isSlow = (since: number | null, now: number) => since !== null && now - since >= SLOW_AFTER_MS;

/**
 * Ручное нажатие, где сервер привязал свою готовую карточку слова из другого модуля и не применил введённые
 * переводы: по контракту `POST /cards/manual` такой элемент пишется без выбора (`picks = []`,
 * `docs/project/backend.md`), а у новой ручной карточки выбор — введённые переводы. Запасной признак для облачка
 * «Слово уже есть…» после «Добавить», когда ответ не несёт `reused` (бэкенд до приёмки A2-03): новый сервер
 * отдаёт `reused` и выводит вид записи из карточки (у карточки ИИ — `word`), подпись записи берётся из вида.
 */
export const isReusedManual = (summary: Pick<GenerationSummary, 'kind' | 'counts'>) =>
  summary.kind === 'manual' && summary.counts.total > 0 && summary.counts.picks === 0;

export interface RecordStatusView {
  glyph: GlyphKind;
  /** Идёт: кольцо крутится, по строке блик, фон тёплый. */
  live: boolean;
  /** Все карточки удалены: строка приглушена и не раскрывается. */
  removed: boolean;
  /** Тон статуса во второй строке. */
  tone: StatusTone;
  /** Видимый статус во второй строке, перед «→ модуль». */
  label: TextRef;
  /** Хвост «: 1 из 3» отдельным элементом — до 390 px его прячет CSS. */
  tail: TextRef | null;
  /** Статус словами для `aria-label` заголовка: части через запятую («готово, 3 карточки»). */
  aria: TextRef[];
}

/** Свёрнутая запись (ТЗ § 22.4): значок, статус и число карточек во второй строке. */
export function recordStatusView(
  summary: GenerationSummary,
  { now, slowSince }: { now: number; slowSince: number | null },
): RecordStatusView {
  const { counts } = summary;
  if (isGenerationActive(summary)) {
    const label: TextRef = isSlow(slowSince, now)
      ? { key: 'generations.status.slow' }
      : counts.ready > 0 && counts.total > 0
        ? { key: 'generations.status.progress', params: { done: counts.ready, total: counts.total } }
        : { key: 'generations.status.generating' };
    return {
      glyph: 'generating',
      live: true,
      removed: false,
      tone: label.key === 'generations.status.slow' ? 'warn' : 'accent',
      label,
      tail: null,
      aria: [label],
    };
  }
  switch (summary.status) {
    case 'ready': {
      // Вид записи сервер выводит из карточки (`summary_kind`): ручное нажатие с карточкой ИИ (переиспользованной
      // из другого модуля или заново сгенерированной) приходит как `word` — «1 карточка», а не «вручную» (A2-03).
      const manual = summary.kind === 'manual';
      const label: TextRef = manual
        ? { key: 'generations.manual' }
        : { key: 'generations.cards', params: { count: counts.total } };
      return {
        glyph: 'ready',
        live: false,
        removed: false,
        tone: 'plain',
        label,
        tail: null,
        aria: manual ? [label] : [{ key: 'generations.status.ready' }, label],
      };
    }
    case 'removed':
      return {
        glyph: 'removed',
        live: false,
        removed: true,
        tone: 'plain',
        label: { key: 'generations.status.removed' },
        tail: null,
        aria: [{ key: 'generations.status.removed' }],
      };
    default: {
      // partial или failed. Хвост «: N из M» — только когда упали элементы: у недобравшихся
      // значений готового слова счёт «0 из 1» был бы неправдой.
      const of = { failed: counts.failed, total: counts.total };
      const withTail = summary.status === 'partial' && counts.failed > 0 && counts.total > 0;
      return {
        glyph: 'failed',
        live: false,
        removed: false,
        tone: 'warn',
        label: { key: 'generations.status.failed' },
        tail: withTail ? { key: 'generations.status.failedTail', params: of } : null,
        aria: [withTail ? { key: 'generations.status.failedOf', params: of } : { key: 'generations.status.failed' }],
      };
    }
  }
}

export type ItemState =
  | 'ready'
  | 'generating'
  | 'extending'
  | 'selectionFailed'
  | 'failed'
  | 'restricted'
  | 'typo'
  | 'garbage'
  | 'removed'
  | 'skipped';

export type ItemAction = 'retry' | 'manual' | 'fix' | 'reenter';

export interface ItemStatusView {
  state: ItemState;
  /** Статус словами (14 px). У готового элемента — null. */
  label: TextRef | null;
  tone: StatusTone;
  /** Кольцо 12 px рядом со статусом — пока идёт. */
  ring: boolean;
  /** Причина под статусом: ключ текста или текст сервера. */
  reason: TextRef | { text: string } | null;
  /** Действие справа. У готового элемента с недобравшимися значениями — «Ввести снова» в первой такой строке. */
  action: ItemAction | null;
  /** Строка элемента («что | статус и причина | действие») вместо строк значений. */
  itemRow: boolean;
  /** Строка приглушена (удалена). */
  dim: boolean;
}

/** Строка элемента в раскрытой записи (ТЗ § 22.5, таблица статусов элемента). */
export function itemStatusView(
  item: GenerationItem,
  { now, slowSince }: { now: number; slowSince: number | null },
): ItemStatusView {
  const base: ItemStatusView = { state: 'ready', label: null, tone: 'plain', ring: false, reason: null, action: null, itemRow: false, dim: false };
  // Идущий элемент без значений (сервер не прислал выбор — старый путь создания без `selected`, например
  // замена слова при опечатке на /cards/new): строк значений нет, поэтому статус — строкой элемента.
  const noRows = item.meanings.every((meaning) => meaning.senses.length === 0);
  switch (item.status) {
    case 'generating': {
      const slow = isSlow(slowSince, now);
      return {
        ...base,
        state: 'generating',
        label: { key: slow ? 'generations.item.slow' : 'generations.item.generating' },
        tone: slow ? 'warn' : 'accent',
        ring: true,
        itemRow: noRows,
      };
    }
    case 'extending':
      return { ...base, state: 'extending', label: { key: 'generations.item.extending' }, tone: 'accent', ring: true, itemRow: noRows };
    case 'ready':
      return { ...base, action: item.failedPicks.length > 0 ? 'reenter' : null };
    case 'selection_failed':
      return { ...base, state: 'selectionFailed', label: { key: 'generations.item.notAdded' }, tone: 'warn', action: 'reenter', itemRow: true };
    case 'failed': {
      if (item.problem?.code === 'restricted') {
        // Отказ модерации: повтор заведомо упадёт — вместо «Повторить» «Добавить вручную».
        return {
          ...base,
          state: 'restricted',
          label: { key: 'generations.item.failed' },
          tone: 'warn',
          reason: { key: 'generations.item.restricted' },
          action: 'manual',
          itemRow: true,
        };
      }
      // Причина — свой текст по коду, на любом языке: тексты сервера пока не по голосу продукта («AI-сервис…»,
      // «сгенерировать карточку», «Квота возвращена»). `problem.message` — когда бэкенд их перепишет (приёмка A2-09).
      return {
        ...base,
        state: 'failed',
        label: { key: 'generations.item.failed' },
        tone: 'warn',
        reason: { key: 'generations.item.failedReason' },
        action: 'retry',
        itemRow: true,
      };
    }
    case 'typo': {
      const suggestion = item.problem?.suggestions.find((s) => s.trim());
      return {
        ...base,
        state: 'typo',
        label: { key: 'generations.item.typo' },
        tone: 'warn',
        reason: suggestion ? { key: 'generations.item.suggest', params: { word: suggestion } } : null,
        action: 'fix',
        itemRow: true,
      };
    }
    case 'garbage':
      return { ...base, state: 'garbage', label: { key: 'generations.item.garbage' }, tone: 'warn', action: 'fix', itemRow: true };
    case 'removed':
      return { ...base, state: 'removed', label: { key: 'generations.item.removed' }, tone: 'muted', itemRow: true, dim: true };
    case 'skipped':
      return { ...base, state: 'skipped', label: { key: skipReasonKey(item.skipReason) }, tone: 'muted' };
  }
}

const SKIP_REASONS = new Set(['already_added', 'not_in_dictionary', 'not_in_topic', 'quota', 'in_progress', 'error']);

/** Причина пропуска: известные — своим текстом, новые от сервера — «не создана». */
export const skipReasonKey = (reason: string | null) =>
  `generations.skip.${reason && SKIP_REASONS.has(reason) ? reason : 'other'}`;

export type TokenLineView =
  | { key: 'generations.token.none' }
  | { key: 'generations.token.refunded' }
  | { key: 'generations.token.refundedCount'; count: number }
  | { key: 'generations.token.spent'; count: number }
  | { key: 'generations.token.checked'; count: number };

/**
 * Строка о жетоне нажатия в раскрытой записи (ТЗ § 23.2) — по держателям жетона в детали
 * (элементы с `token`), первое подходящее правило. Только у законченной записи с неудачами
 * (`partial` или `failed`, не ручной): пока идёт генерация, «ушёл» или «вернулся» были бы
 * преждевременны. Цена «Повторить» — на кнопке, в `token` её нет.
 */
export function tokenLine(detail: Pick<GenerationDetail, 'kind' | 'status' | 'items'>): TokenLineView | null {
  if (detail.kind === 'manual' || (detail.status !== 'partial' && detail.status !== 'failed')) return null;
  const holders = detail.items.filter((item) => item.token !== null);
  // 1. Держателя нет: выбраны только готовые значения или «попутчик» прошлого нажатия.
  if (holders.length === 0) return { key: 'generations.token.none' };
  const refunded = holders.filter((item) => item.token === 'refunded').length;
  if (refunded > 0) {
    // 2. Один держатель вернул жетон. 3. Подборка: пишем только вернувшиеся.
    return holders.length === 1 ? { key: 'generations.token.refunded' } : { key: 'generations.token.refundedCount', count: refunded };
  }
  // Только held.
  if (holders.some((item) => item.status === 'ready' || item.status === 'removed')) {
    return { key: 'generations.token.spent', count: holders.length };
  }
  if (holders.every((item) => item.status === 'typo' || item.status === 'garbage')) {
    return { key: 'generations.token.checked', count: holders.length };
  }
  // 6. held у упавшего держателя — сервер не записал возврат: не гадаем.
  return null;
}

/**
 * Причина сбоя карточки (`error_info`), записанная бэкендом до перевода текстов на голос продукта: «AI-сервис не
 * ответил вовремя — попробуй сгенерировать карточку ещё раз.», «…AI-сервис отказался его обрабатывать…». У таких
 * карточек экран ошибки показывает ту же причину, что запись «Недавно добавленных» (приёмка главной D, A2-09).
 * Новый текст сервера — null: его показывают как есть.
 */
export function legacyFailureReasonKey(message: string | null | undefined): string | null {
  if (!message) return null;
  if (message.startsWith('AI-сервис не ответил вовремя')) return 'generations.item.failedReason';
  if (message.includes('AI-сервис отказался')) return 'generations.item.restricted';
  return null;
}
